import { TranslateModule } from '@ngx-translate/core';
import { ICurrencyModel } from '@services/api/currency/currency-model.interface';
import { IInvoiceStatusModel } from '@services/api/invoice-status/invoice-status-model.interface';
import { SelectItemLoaderService, UiSelectItem } from '@services/util/select-item-loader.service';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DatePickerModule } from 'primeng/datepicker';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Observable, Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InvoiceFilter } from '../models/invoice-filter.model';

@Component({
  selector: 'app-invoice-filters',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DatePickerModule,
    SelectModule,
    CardModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    AccordionModule,
    SelectButtonModule
  ],
  template: `
    <div class="mb-4 flex flex-col gap-4">
      <form
        [formGroup]="filterForm"
        class="flex flex-col gap-4"
      >
        <!-- Always-visible quick search (search_term): OR-search across invoice no., guest, booking, source, unit and billing address -->
        <div class="card mb-0">
          <span class="mb-2 block text-base font-semibold">{{ 'invoices.filters.searchTerm' | translate }}</span>
          <p-inputGroup class="w-full">
            <p-inputGroupAddon>
              <i class="pi pi-search"></i>
            </p-inputGroupAddon>
            <input
              type="text"
              pInputText
              formControlName="searchTerm"
              class="w-full"
              [placeholder]="'invoices.filters.searchTermPlaceholder' | translate"
              (keydown.enter)="applyFilters()"
            />
            @if (filterForm.get('searchTerm')?.value) {
              <p-inputGroupAddon>
                <button
                  type="button"
                  class="p-inputgroup-addon cursor-pointer"
                  (click)="clearFilterField('searchTerm')"
                >
                  <i class="pi pi-times"></i>
                </button>
              </p-inputGroupAddon>
            }
            <button
              pButton
              type="button"
              icon="pi pi-search"
              [label]="'common.search' | translate"
              (click)="applyFilters()"
            ></button>
          </p-inputGroup>
        </div>

        <!-- Detailed filters (collapsible) -->
        <p-accordion
          [multiple]="false"
          [activeIndex]="isExpanded ? 0 : -1"
          (onClose)="isExpanded = false"
          (onOpen)="isExpanded = true"
        >
          <p-accordion-panel value="0">
            <p-accordion-header>
              <div class="flex items-center gap-2">
                <i class="pi pi-sliders-h"></i>
                <span>{{ 'invoices.filters.advancedTitle' | translate }}</span>
                @if (hasActiveFilters()) {
                  <span class="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-white">{{
                    countActiveFilters()
                  }}</span>
                }
              </div>
            </p-accordion-header>
            <p-accordion-content>
              <div class="flex flex-col gap-4">
              <!-- Invoice Number Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'invoices.filters.invoiceNumber' | translate }}</h5>
                <div>
                  <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                    <p-inputGroupAddon>
                      <i class="pi pi-search"></i>
                    </p-inputGroupAddon>
                    <input
                      type="text"
                      pInputText
                      formControlName="invoiceNumber"
                      class="w-full"
                      [placeholder]="'filters.searchPlaceholder' | translate"
                      (keydown.enter)="applyFilters()"
                    />
                    @if (filterForm.get('invoiceNumber')?.value) {
                      <p-inputGroupAddon>
                        <button
                          type="button"
                          class="p-inputgroup-addon cursor-pointer"
                          (click)="clearFilterField('invoiceNumber')"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </p-inputGroupAddon>
                    }
                  </p-inputGroup>
                </div>
              </div>

              <!-- Paid Status Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'invoices.filters.paidStatus' | translate }}</h5>
                <div class="w-full sm:w-2/3 lg:w-1/2">
                  <p-selectButton
                    formControlName="paid"
                    [options]="paidStatusOptions"
                    optionLabel="label"
                    optionValue="value"
                    styleClass="w-full"
                  >
                    <ng-template
                      pTemplate="item"
                      let-option
                    >
                      {{ option.label | translate }}
                    </ng-template>
                  </p-selectButton>
                </div>
              </div>

              <!-- Invoice Date Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'invoices.filters.invoiceDate' | translate }}</h5>
                <div class="flex flex-col gap-3 sm:flex-row">
                  <div class="w-full sm:w-1/3 lg:w-1/4">
                    <p-select
                      formControlName="invoiceDateOperator"
                      [options]="dateOperatorOptions"
                      optionLabel="label"
                      optionValue="value"
                      styleClass="w-full"
                      appendTo="body"
                    >
                      <ng-template
                        pTemplate="selectedItem"
                        let-item
                      >
                        {{ item.label | translate }}
                      </ng-template>
                      <ng-template
                        pTemplate="item"
                        let-option
                      >
                        {{ option.label | translate }}
                      </ng-template>
                    </p-select>
                  </div>
                  <div class="w-full sm:w-2/3 lg:w-1/3">
                    <p-datepicker
                      formControlName="invoiceDate"
                      styleClass="w-full"
                      [showIcon]="true"
                      [dateFormat]="dateFormat"
                      [placeholder]="'filters.datePlaceholder' | translate"
                      appendTo="body"
                    >
                    </p-datepicker>
                  </div>
                </div>
              </div>

              <!-- Fulfillment Date Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'invoices.filters.fulfillmentDate' | translate }}</h5>
                <div class="flex flex-col gap-3 sm:flex-row">
                  <div class="w-full sm:w-1/3 lg:w-1/4">
                    <p-select
                      formControlName="fulfillmentDateOperator"
                      [options]="dateOperatorOptions"
                      optionLabel="label"
                      optionValue="value"
                      styleClass="w-full"
                      appendTo="body"
                    >
                      <ng-template
                        pTemplate="selectedItem"
                        let-item
                      >
                        {{ item.label | translate }}
                      </ng-template>
                      <ng-template
                        pTemplate="item"
                        let-option
                      >
                        {{ option.label | translate }}
                      </ng-template>
                    </p-select>
                  </div>
                  <div class="w-full sm:w-2/3 lg:w-1/3">
                    <p-datepicker
                      formControlName="fulfillmentDate"
                      styleClass="w-full"
                      [showIcon]="true"
                      [dateFormat]="dateFormat"
                      [placeholder]="'filters.datePlaceholder' | translate"
                      appendTo="body"
                    >
                    </p-datepicker>
                  </div>
                </div>
              </div>

              <!-- Due Date Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'invoices.filters.dueDate' | translate }}</h5>
                <div class="flex flex-col gap-3 sm:flex-row">
                  <div class="w-full sm:w-1/3 lg:w-1/4">
                    <p-select
                      formControlName="dueDateOperator"
                      [options]="dateOperatorOptions"
                      optionLabel="label"
                      optionValue="value"
                      styleClass="w-full"
                      appendTo="body"
                    >
                      <ng-template
                        pTemplate="selectedItem"
                        let-item
                      >
                        {{ item.label | translate }}
                      </ng-template>
                      <ng-template
                        pTemplate="item"
                        let-option
                      >
                        {{ option.label | translate }}
                      </ng-template>
                    </p-select>
                  </div>
                  <div class="w-full sm:w-2/3 lg:w-1/3">
                    <p-datepicker
                      formControlName="dueDate"
                      styleClass="w-full"
                      [showIcon]="true"
                      [dateFormat]="dateFormat"
                      [placeholder]="'filters.datePlaceholder' | translate"
                      appendTo="body"
                    >
                    </p-datepicker>
                  </div>
                </div>
              </div>

              <!-- Customer Name Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'invoices.filters.customerName' | translate }}</h5>
                <div>
                  <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                    <p-inputGroupAddon>
                      <i class="pi pi-user"></i>
                    </p-inputGroupAddon>
                    <input
                      type="text"
                      pInputText
                      formControlName="customerName"
                      class="w-full"
                      [placeholder]="'invoices.filters.customerNamePlaceholder' | translate"
                      (keydown.enter)="applyFilters()"
                    />
                    @if (filterForm.get('customerName')?.value) {
                      <p-inputGroupAddon>
                        <button
                          type="button"
                          class="p-inputgroup-addon cursor-pointer"
                          (click)="clearFilterField('customerName')"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </p-inputGroupAddon>
                    }
                  </p-inputGroup>
                </div>
              </div>

              <!-- Customer Email Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'invoices.filters.customerEmail' | translate }}</h5>
                <div>
                  <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                    <p-inputGroupAddon>
                      <i class="pi pi-envelope"></i>
                    </p-inputGroupAddon>
                    <input
                      type="email"
                      pInputText
                      formControlName="customerEmail"
                      class="w-full"
                      [placeholder]="'invoices.filters.customerEmailPlaceholder' | translate"
                      (keydown.enter)="applyFilters()"
                    />
                    @if (filterForm.get('customerEmail')?.value) {
                      <p-inputGroupAddon>
                        <button
                          type="button"
                          class="p-inputgroup-addon cursor-pointer"
                          (click)="clearFilterField('customerEmail')"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </p-inputGroupAddon>
                    }
                  </p-inputGroup>
                </div>
              </div>

              <!-- Invoice Status Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'invoices.filters.invoiceStatus' | translate }}</h5>
                <div class="w-full md:w-2/3 lg:w-1/2">
                  <p-select
                    formControlName="invoiceStatus"
                    [options]="(invoiceStatusItems$ | async) || []"
                    optionLabel="explicitTitle"
                    optionValue="model.id"
                    styleClass="w-full"
                    [placeholder]="'invoices.filters.invoiceStatusPlaceholder' | translate"
                    [showClear]="true"
                    appendTo="body"
                  >
                  </p-select>
                </div>
              </div>

              <!-- Currency Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'invoices.filters.currency' | translate }}</h5>
                <div class="w-full md:w-2/3 lg:w-1/2">
                  <p-select
                    formControlName="currency"
                    [options]="(currencyItems$ | async) || []"
                    optionLabel="explicitTitle"
                    optionValue="model.id"
                    styleClass="w-full"
                    [placeholder]="'invoices.filters.currencyPlaceholder' | translate"
                    [showClear]="true"
                    appendTo="body"
                  >
                  </p-select>
                </div>
              </div>

              <!-- Booking Name Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'invoices.filters.bookingName' | translate }}</h5>
                <div>
                  <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                    <p-inputGroupAddon>
                      <i class="pi pi-calendar"></i>
                    </p-inputGroupAddon>
                    <input
                      type="text"
                      pInputText
                      formControlName="bookingName"
                      class="w-full"
                      [placeholder]="'invoices.filters.bookingNamePlaceholder' | translate"
                      (keydown.enter)="applyFilters()"
                    />
                    @if (filterForm.get('bookingName')?.value) {
                      <p-inputGroupAddon>
                        <button
                          type="button"
                          class="p-inputgroup-addon cursor-pointer"
                          (click)="clearFilterField('bookingName')"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </p-inputGroupAddon>
                    }
                  </p-inputGroup>
                </div>
              </div>

              <!-- User Note Filter -->
              <div class="max-w-screen-lg">
                <h5 class="mb-2 text-base font-medium">{{ 'invoices.filters.userNote' | translate }}</h5>
                <div>
                  <p-inputGroup class="w-full md:w-2/3 lg:w-1/2">
                    <p-inputGroupAddon>
                      <i class="pi pi-comment"></i>
                    </p-inputGroupAddon>
                    <input
                      type="text"
                      pInputText
                      formControlName="userNote"
                      class="w-full"
                      [placeholder]="'invoices.filters.userNotePlaceholder' | translate"
                      (keydown.enter)="applyFilters()"
                    />
                    @if (filterForm.get('userNote')?.value) {
                      <p-inputGroupAddon>
                        <button
                          type="button"
                          class="p-inputgroup-addon cursor-pointer"
                          (click)="clearFilterField('userNote')"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </p-inputGroupAddon>
                    }
                  </p-inputGroup>
                </div>
              </div>

              <!-- Filter Actions -->
              <div class="flex justify-end">
                <div class="flex gap-3">
                  <button
                    pButton
                    type="button"
                    [label]="'common.reset' | translate"
                    class="p-button-outlined"
                    (click)="resetAllFilters()"
                  ></button>

                  <button
                    pButton
                    type="button"
                    [label]="'common.apply' | translate"
                    class="p-button-primary"
                    (click)="applyFilters()"
                  ></button>
                </div>
              </div>
              </div>
            </p-accordion-content>
          </p-accordion-panel>
        </p-accordion>
      </form>
    </div>
  `
})
export class InvoiceFiltersComponent implements OnInit, OnDestroy {
  @Input() activeFilter: InvoiceFilter | undefined;
  @Output() filterChange = new EventEmitter<InvoiceFilter>();

  private fb = inject(FormBuilder);
  private selectItemLoader = inject(SelectItemLoaderService);
  private destroy$ = new Subject<void>();

  filterForm!: FormGroup;
  isExpanded = false;

  dateFormat = 'yy-mm-dd';

  // SILO dropdown observables
  invoiceStatusItems$: Observable<UiSelectItem<IInvoiceStatusModel>[]>;
  currencyItems$: Observable<UiSelectItem<ICurrencyModel>[]>;

  constructor() {
    this.invoiceStatusItems$ = this.selectItemLoader.getInvoiceStatusSelectItems();
    this.currencyItems$ = this.selectItemLoader.getCurrencySelectItems();
  }

  dateOperatorOptions = [
    { value: 'exact', label: 'filters.exact' },
    { value: 'before', label: 'filters.before' },
    { value: 'after', label: 'filters.after' },
    { value: 'strictly_before', label: 'filters.strictlyBefore' },
    { value: 'strictly_after', label: 'filters.strictlyAfter' }
  ];

  paidStatusOptions = [
    { value: null, label: 'filters.all' },
    { value: true, label: 'common.yes' },
    { value: false, label: 'common.no' }
  ];

  ngOnInit(): void {
    this.initForm();
    if (this.activeFilter) {
      this.populateForm();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.filterForm = this.fb.group({
      searchTerm: [''],
      invoiceNumber: [''],
      paid: [null],
      invoiceDate: [null],
      invoiceDateOperator: ['exact'],
      fulfillmentDate: [null],
      fulfillmentDateOperator: ['exact'],
      dueDate: [null],
      dueDateOperator: ['exact'],

      // New filter fields
      customerName: [''],
      customerEmail: [''],
      invoiceStatus: [null],
      bookingName: [''],
      userNote: [''],
      currency: [null]
    });
  }

  private populateForm(): void {
    if (!this.activeFilter) return;

    this.filterForm.patchValue({
      searchTerm: this.activeFilter.searchTerm || '',
      invoiceNumber: this.activeFilter.invoiceNumber || '',
      paid: this.activeFilter.paid ?? null,
      invoiceDate: this.activeFilter.invoiceDate?.date || null,
      invoiceDateOperator: this.activeFilter.invoiceDate?.operator || 'exact',
      fulfillmentDate: this.activeFilter.fulfillmentDate?.date || null,
      fulfillmentDateOperator: this.activeFilter.fulfillmentDate?.operator || 'exact',
      dueDate: this.activeFilter.dueDate?.date || null,
      dueDateOperator: this.activeFilter.dueDate?.operator || 'exact',

      // New filter fields
      customerName: this.activeFilter['customer_user.name'] || '',
      customerEmail: this.activeFilter['customer_user.email'] || '',
      invoiceStatus: this.activeFilter['invoiceStatus.id'] || null,
      bookingName: this.activeFilter['bookings.name'] || '',
      userNote: this.activeFilter['user_note'] || '',
      currency: this.activeFilter['currency.id'] || null
    });
  }

  clearFilterField(fieldName: string): void {
    this.filterForm.get(fieldName)?.setValue('');
  }

  applyFilters(): void {
    if (!this.filterForm) return;

    const formValue = this.filterForm.value;
    const filters: InvoiceFilter = {};

    if (formValue.searchTerm) {
      filters.searchTerm = formValue.searchTerm.trim();
    }

    if (formValue.invoiceNumber) {
      filters.invoiceNumber = formValue.invoiceNumber.trim();
    }

    if (formValue.paid !== null) {
      filters.paid = formValue.paid;
    }

    if (formValue.invoiceDate) {
      filters.invoiceDate = {
        date: formValue.invoiceDate,
        operator: formValue.invoiceDateOperator
      };
    }

    if (formValue.fulfillmentDate) {
      filters.fulfillmentDate = {
        date: formValue.fulfillmentDate,
        operator: formValue.fulfillmentDateOperator
      };
    }

    if (formValue.dueDate) {
      filters.dueDate = {
        date: formValue.dueDate,
        operator: formValue.dueDateOperator
      };
    }

    // New filter fields
    if (formValue.customerName) {
      filters['customer_user.name'] = formValue.customerName.trim();
    }

    if (formValue.customerEmail) {
      filters['customer_user.email'] = formValue.customerEmail.trim();
    }

    if (formValue.invoiceStatus) {
      filters['invoiceStatus.id'] = formValue.invoiceStatus;
    }

    if (formValue.bookingName) {
      filters['bookings.name'] = formValue.bookingName.trim();
    }

    if (formValue.userNote) {
      filters['user_note'] = formValue.userNote.trim();
    }

    if (formValue.currency) {
      filters['currency.id'] = formValue.currency;
    }

    this.filterChange.emit(filters);
  }

  resetAllFilters(): void {
    this.filterForm.reset({
      searchTerm: '',
      invoiceNumber: '',
      paid: null,
      invoiceDate: null,
      invoiceDateOperator: 'exact',
      fulfillmentDate: null,
      fulfillmentDateOperator: 'exact',
      dueDate: null,
      dueDateOperator: 'exact',

      // New filter fields
      customerName: '',
      customerEmail: '',
      invoiceStatus: null,
      bookingName: '',
      userNote: '',
      currency: null
    });

    this.filterChange.emit({});
  }

  hasActiveFilters(): boolean {
    return this.countActiveFilters() > 0;
  }

  countActiveFilters(): number {
    const formValue = this.filterForm?.value;
    if (!formValue) return 0;

    let count = 0;

    // searchTerm is intentionally excluded: it lives in the always-visible quick search, not the detailed panel
    if (formValue.invoiceNumber) count++;
    if (formValue.paid !== null) count++;
    if (formValue.invoiceDate) count++;
    if (formValue.fulfillmentDate) count++;
    if (formValue.dueDate) count++;

    // New filter fields
    if (formValue.customerName) count++;
    if (formValue.customerEmail) count++;
    if (formValue.invoiceStatus) count++;
    if (formValue.bookingName) count++;
    if (formValue.userNote) count++;
    if (formValue.currency) count++;

    return count;
  }
}
